import React, { useState, useRef, useEffect } from "react";
import { Award, X } from "lucide-react";
import { SKILL_GROUPS, groupSkillsByCategory } from "@/lib/skillsTaxonomy";

// Multi-language labels for the skills UI (SK, EN, CS, DE, PL).
const T = {
  sk: { badge: "Zručnosti", other: "Iné (voľný text)",
        otherPlaceholder: "Napr. WPS/WPQR EN ISO 15614, PED modul, atď.",
        popoverTitle: "Certifikované zručnosti", assign: "admin priraďuje" },
  en: { badge: "Skills", other: "Other (free text)",
        otherPlaceholder: "e.g. WPS/WPQR EN ISO 15614, PED module, etc.",
        popoverTitle: "Certified skills", assign: "assigned by admin" },
  cs: { badge: "Dovednosti", other: "Jiné (volný text)",
        otherPlaceholder: "Např. WPS/WPQR EN ISO 15614, PED modul, atd.",
        popoverTitle: "Certifikované dovednosti", assign: "přiděluje admin" },
  de: { badge: "Fähigkeiten", other: "Sonstiges (Freitext)",
        otherPlaceholder: "z. B. WPS/WPQR EN ISO 15614, PED-Modul usw.",
        popoverTitle: "Zertifizierte Fähigkeiten", assign: "vom Admin vergeben" },
  pl: { badge: "Umiej.", other: "Inne (tekst dowolny)",
        otherPlaceholder: "Np. WPS/WPQR EN ISO 15614, moduł PED, itd.",
        popoverTitle: "Certyfikowane umiejętności", assign: "przydziela admin" },
  fr: {
  badge: "Compétences",
  other: "Autre (texte libre)",
  otherPlaceholder: "Ex. WPS/WPQR EN ISO 15614, module PED, etc.",
  popoverTitle: "Compétences certifiées",
  assign: "admin attribue"
},
};
const tt = (lang) => T[lang] || T.sk;

/** Admin-only picker — pill buttons grouped by category. */
export function SkillsPicker({ selected = [], onChange, other = "", onOtherChange, lang = "sk" }) {
  const set = new Set(selected || []);
  const L = tt(lang);
  const toggle = (id) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id); else next.add(id);
    onChange(Array.from(next));
  };
  return (
    <div className="space-y-4" data-testid="skills-picker">
      {SKILL_GROUPS.map((g) => (
        <div key={g.id}>
          <div className="text-[10px] font-mono uppercase tracking-widest text-gold-500 mb-2">
            {g.icon} {g.labels[lang] || g.labels.sk}
          </div>
          <div className="flex flex-wrap gap-2">
            {g.skills.map((s) => {
              const on = set.has(s.id);
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => toggle(s.id)}
                  data-testid={`skill-pill-${s.id}`}
                  className={`px-3 py-1.5 text-xs font-mono border transition-colors ${
                    on ? "border-gold-500 bg-gold-900/25 text-gold-300"
                       : "border-zinc-700 text-zinc-400 hover:border-zinc-500"
                  }`}
                >
                  {s.labels[lang] || s.labels.sk}
                </button>
              );
            })}
          </div>
        </div>
      ))}
      {onOtherChange && <div>
        <label className="block text-[10px] font-mono uppercase tracking-widest text-gold-500 mb-2">
          ➕ {L.other}
        </label>
        <input
          value={other}
          onChange={(e) => onOtherChange(e.target.value)}
          maxLength={300}
          placeholder={L.otherPlaceholder}
          className="wb-input w-full px-3 py-2 text-xs font-mono"
          data-testid="skills-other-input"
        />
      </div>}
    </div>
  );
}

/** Public read-only badge with popover. Click → shows the skill list grouped. */
export function SkillsBadge({ skills = [], skillsOther, lang = "sk" }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const L = tt(lang);
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener("click", onDoc);
    return () => document.removeEventListener("click", onDoc);
  }, [open]);

  const total = (skills || []).length + (skillsOther ? 1 : 0);
  if (total === 0) return null;

  const grouped = groupSkillsByCategory(skills || [], lang);
  // Attach localized group titles (icon + label from taxonomy).
  const groupsWithTitle = grouped.map((g) => {
    const src = SKILL_GROUPS.find((x) => x.id === g.groupId);
    const label = src ? (src.labels[lang] || src.labels.sk) : "";
    return { ...g, title: `${g.icon} ${label}` };
  });

  return (
    <span className="relative inline-flex" ref={ref}>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
        className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest border border-gold-700/50 text-gold-400 hover:border-gold-500 hover:text-gold-300 transition-colors"
        data-testid="skills-badge-btn"
        aria-label={L.badge}
      >
        <Award className="w-3 h-3" /> {L.badge} ({total})
      </button>
      {open && (
        <div
          className="absolute left-0 top-full mt-2 z-30 w-72 max-w-[calc(100vw-2rem)] bg-black border border-gold-700/50 shadow-2xl p-4"
          data-testid="skills-badge-popover"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] font-mono uppercase tracking-widest text-gold-500">
              {L.popoverTitle}
            </div>
            <button onClick={() => setOpen(false)} className="text-zinc-500 hover:text-zinc-300">
              <X className="w-3 h-3" />
            </button>
          </div>
          <div className="space-y-3">
            {groupsWithTitle.map((g) => (
              <div key={g.groupId}>
                <div className="text-[10px] text-zinc-500 mb-1">{g.title}</div>
                <div className="flex flex-wrap gap-1">
                  {g.items.map((it) => (
                    <span key={it.id} className="px-1.5 py-0.5 text-[10px] font-mono border border-gold-800/40 text-gold-300/90 bg-gold-950/20">
                      {it.label}
                    </span>
                  ))}
                </div>
              </div>
            ))}
            {skillsOther && (
              <div className="pt-2 border-t border-zinc-800">
                <div className="text-[10px] text-zinc-500 mb-1">➕</div>
                <div className="text-[11px] text-gold-300/90 leading-relaxed">{skillsOther}</div>
              </div>
            )}
          </div>
        </div>
      )}
    </span>
  );
}
